import { DTO__grid2d_cell } from '@shared/dto/grid2d_cell/DTO__grid2d_cell';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO__database_entity } from '../_database_entity/DTO__database_entity';
// import { assert_condition, assert_false, assert_not_null, assert_number_lte, assert_string_not_empty } from '@shared/utils/utils_asserts';
import {
  assert_false,
  assert_not_null,
  assert_number_gte,
  assert_number_gte_0,
  assert_number_lte,
  assert_string_not_empty,
  assert_type_enum,
} from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__workspace_entity } from '../+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { DTO__grid2d_chunk } from '../grid2d_chunk/DTO__grid2d_chunk';

//grid2ds  contains unique list of cells by image_id
//grid2d sends always with all chunks
export class DTO__grid2d extends DTO__database_entity {
  static _class_name_static: string = ENUM__dto_class_name.GRID2D;

  /*
   * STATIC FIELDS
   */

  static readonly KEYS_RESTRICTED_FOR_PARTIAL_UPDATE = [
    'workspace_id',
    'user_id_creator',
    'entity_type',
    'chunks',
    '_id',
    CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE,
  ] as const satisfies (keyof DTO__grid2d)[];

  /*
   * DB FIELDS
   */

  workspace_id!: string;
  user_id_creator!: string;
  entity_type!: ENUM__workspace_entity;

  /*
   * SERVER RUNTIME FIELDS
   */
  //sorted by chunk_index
  chunks: DTO__grid2d_chunk[];
  //key - cell index, value - chunk index
  cell_index_to_chunk_index_map: Map<number, number>;

  total_cells_amount!: number;
  total_images_amount!: number;

  /*
   * EVENTS
   */
  on_grid2d_updated() {
    this.chunks.sort(DTO__database_entity.sort_by_created_at_oldest_first);

    this.total_cells_amount = 0;
    this.total_images_amount = 0;

    //need recreate map
    this.cell_index_to_chunk_index_map = new Map();

    for (let index_chunk = 0; index_chunk < this.chunks.length; index_chunk++) {
      let chunk = this.chunks[index_chunk];

      chunk.chunk_index = index_chunk; // for user task we need set chunk_index after receive chunk

      chunk.cells_amount_before_this_chunk = this.total_cells_amount;

      this.total_cells_amount += chunk.cells_amount;
      this.total_images_amount += chunk.images_amount;

      let chunk_current_index_min = chunk.cells_amount_before_this_chunk;
      let chunk_current_index_max = chunk_current_index_min + chunk.cells_amount - 1; // amount != index

      for (let index_cell = chunk_current_index_min; index_cell <= chunk_current_index_max; index_cell++) {
        this.cell_index_to_chunk_index_map.set(index_cell, index_chunk);
      }
    }
  }

  /*
   * INSTANCE METHODS
   */

  constructor() {
    super();

    this.chunks = [];
    this.cell_index_to_chunk_index_map = new Map();
  }

  init_total_amount_of_images_and_cells() {
    this.total_cells_amount = 0;
    this.total_images_amount = 0;

    for (let chunk of this.chunks) {
      this.total_cells_amount += chunk.cells_amount;
      this.total_images_amount += chunk.images_amount;
    }
  }

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_string_not_empty(this.workspace_id);
    assert_string_not_empty(this.user_id_creator);
    assert_type_enum(this.entity_type, ENUM__workspace_entity);

    assert_number_gte_0(this.total_cells_amount);
    assert_number_gte_0(this.total_images_amount);
  }

  /*
   *
   */

  chunk__get_by_cell_index(cell_index_absolute: number) {
    assert_number_lte(cell_index_absolute, this.cell_index_to_chunk_index_map.size - 1);
    assert_number_gte(cell_index_absolute, 0);

    let chunk_index = this.cell_index_to_chunk_index_map.get_or_assert(cell_index_absolute);

    let out = this.chunks[chunk_index];

    assert_not_null(out);

    return out;
  }

  chunk__get_id_by_index(chunk_index: number) {
    let out = '';

    for (let chunk of this.chunks) {
      if (chunk.chunk_index === chunk_index) {
        out = chunk._id;
        break;
      }
    }

    assert_string_not_empty(out);

    return out;
  }

  chunk__get_by_id_or_assert(chunk_id: string) {
    let out = this.chunk__get_by_id(chunk_id);

    assert_false(out.is_stub());

    return out;
  }

  private chunk__get_by_id(chunk_id: string) {
    let out = this.chunk__create_stub();

    for (let chunk of this.chunks) {
      if (chunk._id == chunk_id) {
        out = chunk;
        break;
      }
    }
    assert_not_null(out);

    return out;
  }

  chunk__create_stub() {
    return DTO__grid2d_chunk.CREATE_STUB();
  }

  chunk__upsert(chunk: DTO__grid2d_chunk) {
    let grid2d_chunk_index = this.chunks.findIndex(item => item._id == chunk._id);

    if (grid2d_chunk_index == -1) {
      this.chunks.push(chunk);
      this.chunks.sort(DTO__grid2d_chunk.sort_by_chunk_index);
    } else {
      this.chunks[grid2d_chunk_index] = chunk;
    }

    this.on_grid2d_updated();
  }

  /*
   *
   */

  cells__get_loaded() {
    let out: DTO__grid2d_cell[] = [];

    for (let chunk of this.chunks) {
      out = [...out, ...chunk.cells_list];
    }

    return out;
  }

  cells__get_list(newest_first: boolean = true): DTO__grid2d_cell[] {
    const out: DTO__grid2d_cell[] = [];

    if (this.is_stub()) return out;

    for (let i = 0; i < this.total_cells_amount; i++) {
      const item = this.cell__get_by_index(i);

      newest_first ? out.unshift(item.clone()) : out.push(item.clone());
    }

    return out;
  }

  cell__get_by_index(cell_index_absolute: number): DTO__grid2d_cell {
    assert_number_lte(0, cell_index_absolute);
    assert_number_lte(cell_index_absolute, this.total_cells_amount - 1);

    let target_chunk = this.chunk__get_by_cell_index(cell_index_absolute);

    let out = target_chunk.get_cell_by_index(cell_index_absolute);

    return out;
  }

  cell__upsert(cell: DTO__grid2d_cell) {
    let target_chunk = this.chunk__get_by_id_or_assert(cell.grid2d_chunk_id);

    target_chunk.upsert_cell(cell);

    this.on_grid2d_updated();
  }

  cell__delete(cell: DTO__grid2d_cell) {
    let target_chunk = this.chunk__get_by_id_or_assert(cell.grid2d_chunk_id);

    target_chunk.delete_cell(cell);

    this.on_grid2d_updated();
  }
}
